import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Power, Clock, CheckCircle } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PSShutdownButton({ unitId, onShutdownComplete }) {
  const [shutdownStatus, setShutdownStatus] = useState(null);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [loading, setLoading] = useState(false);
  const [rentalId, setRentalId] = useState(null);
  const [justCompleted, setJustCompleted] = useState(false); // Prevent re-showing after complete

  useEffect(() => {
    fetchShutdownStatus();

    // Idle units do not need five-second polling. Keep fast polling only while
    // a shutdown is actually running, otherwise refresh at a lighter cadence.
    const pollMs = shutdownStatus === 'in_progress' ? 5000 : 30000;
    const interval = setInterval(fetchShutdownStatus, pollMs);
    return () => clearInterval(interval);
  }, [unitId, shutdownStatus]);

  // Countdown timer
  useEffect(() => {
    if (shutdownStatus === 'in_progress' && remainingSeconds > 0) {
      const timer = setInterval(() => {
        setRemainingSeconds(prev => {
          if (prev <= 1) {
            // Timer expired, fetch new status
            fetchShutdownStatus();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => clearInterval(timer);
    }
  }, [shutdownStatus, remainingSeconds]);

  const fetchShutdownStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/units/${unitId}/ps-shutdown-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = response.data;
      console.log(' Status fetched:', data.status, 'Rental:', data.rental_id);
      
      setShutdownStatus(data.status);
      setRentalId(data.rental_id);
      
      if (data.status === 'in_progress') {
        setRemainingSeconds(data.remaining_seconds || 0);
      }
    } catch (error) {
      console.error(' Error fetching shutdown status:', error);
    }
  };

  const handleStartShutdown = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (loading) return;
    
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/units/${unitId}/start-ps-shutdown`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      toast.success('TV nyala, matikan PS secara manual');
      await fetchShutdownStatus();
    } catch (error) {
      console.error('Error starting shutdown:', error);
      toast.error(error.response?.data?.detail || 'Gagal memulai shutdown PS');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteShutdown = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    const globalLockKey = `__ps_shutdown_lock_${unitId}__`;
    
    if (window[globalLockKey] === true || loading) {
      return;
    }
    
    window[globalLockKey] = true;
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/units/${unitId}/complete-ps-shutdown`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      // ONLY show success toast - NO error toast
      toast.success('PS berhasil dimatikan');
      
      // Fetch status - this will update shutdownStatus to hide button
      await fetchShutdownStatus();
      
      // Set justCompleted AFTER status fetched
      setJustCompleted(true);
      
      if (onShutdownComplete) {
        onShutdownComplete();
      }
    } catch (error) {
      // NO TOAST on error - just log
      console.error('Error:', error);
    } finally {
      setLoading(false);
      setTimeout(() => {
        window[globalLockKey] = false;
      }, 5000);
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Don't show button if no action needed
  if (shutdownStatus === 'no_action_needed') {
    return null;
  }

  // Show "Matikan PS" button
  if (shutdownStatus === 'needs_shutdown') {
    return (
      <Button
        onClick={handleStartShutdown}
        disabled={loading}
        className={`w-full bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600 text-white font-bold shadow-lg transition-all flex items-center justify-center gap-2 ${
          loading ? 'opacity-50 cursor-not-allowed' : 'transform hover:scale-105'
        }`}
      >
        <Power className="h-4 w-4" />
        {loading ? 'Memproses...' : 'Matikan PS'}
      </Button>
    );
  }

  // Show "Selesai" button with countdown (hide after completed)
  if (shutdownStatus === 'in_progress') {
    return (
      <Button
        onClick={handleCompleteShutdown}
        disabled={loading}
        className={`w-full bg-gradient-to-r from-yellow-600 to-yellow-500 hover:from-yellow-700 hover:to-yellow-600 text-white font-bold shadow-lg transition-all flex items-center justify-center gap-2 ${
          loading ? 'opacity-50 cursor-not-allowed' : 'transform hover:scale-105'
        }`}
      >
        {loading ? (
          'Mematikan TV...'
        ) : (
          <>
            <CheckCircle className="h-4 w-4" />
            <span>Selesai</span>
            <Clock className="h-4 w-4 ml-1" />
            <span className="font-mono">{formatTime(remainingSeconds)}</span>
          </>
        )}
      </Button>
    );
  }

  return null;
}
